import { getErrorMessage } from '@/lib/functions';
import { useTranslation } from 'react-i18next';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Share,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { Agreement, Payment } from '@/lib/database.types';
import { getAgreement, getPayment } from '@/lib/agreements';
import { formatAmount, formatDateTime, paymentStatusColor, paymentStatusLabel } from '@/lib/format';
import { ErrorText } from '@/components/ErrorText';

function receiptNumber(id: string): string {
  return `ΧΕΙΡ-${id.slice(0, 8).toUpperCase()}`;
}

export default function ReceiptScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [payment, setPayment] = useState<Payment | null>(null);
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const p = await getPayment(id);
      setPayment(p);
      if (p) setAgreement(await getAgreement(p.agreement_id));
    } catch (e) {
      setError(getErrorMessage(e, t('common.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onShare() {
    if (!payment) return;
    const lines = [
      t('receipt.share_header'),
      t('receipt.share_status', { status: paymentStatusLabel(payment.status) }),
      t('receipt.share_amount', { amount: formatAmount(payment.amount, payment.currency) }),
      t('receipt.share_date', { date: formatDateTime(payment.created_at) }),
      agreement ? t('receipt.share_agreement', { title: agreement.title }) : null,
      payment.wallet_paid ? t('receipt.share_method') : null,
      payment.viva_order_code ? t('receipt.share_order', { code: payment.viva_order_code }) : null,
      payment.viva_transaction_id ? t('receipt.share_transaction', { code: payment.viva_transaction_id }) : null,
      '',
      t('receipt.share_fee'),
    ].filter(Boolean);
    try {
      await Share.share({ message: lines.join('\n') });
    } catch {
      // αγνόησε ακύρωση κοινοποίησης
    }
  }

  if (loading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!payment) {
    return (
      <View style={[styles.loader, { padding: spacing.lg, gap: spacing.md }]}>
        <Text style={styles.notFound}>{t('receipt.notFound')}</Text>
        <Button title={t('common.back')} variant="secondary" onPress={() => router.back()} />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
      ]}
    >
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
      </View>

      <View style={styles.receipt}>
        <View style={styles.receiptHeader}>
          <Text style={styles.brand}>Χειραψία</Text>
          <Text style={styles.receiptLabel}>{t('receipt.label')}</Text>
          <Text style={styles.amount}>{formatAmount(payment.amount, payment.currency)}</Text>
          <Text style={[styles.status, { color: paymentStatusColor(payment.status) }]}>
            {paymentStatusLabel(payment.status)}
          </Text>
        </View>

        <View style={styles.divider} />

        <Row label={t('receipt.field_number')} value={receiptNumber(payment.id)} styles={styles} />
        <Row label={t('receipt.field_date')} value={formatDateTime(payment.created_at)} styles={styles} />
        {agreement && (
          <Row
            label={t('receipt.field_agreement')}
            value={agreement.title}
            onPress={() => router.push({ pathname: '/(app)/agreements/[id]', params: { id: agreement.id } })}
            styles={styles}
          />
        )}
        {payment.wallet_paid && <Row label={t('receipt.field_method')} value={t('receipt.wallet')} styles={styles} />}
        {!!payment.viva_order_code && (
          <Row label={t('receipt.field_orderCode')} value={payment.viva_order_code} styles={styles} />
        )}
        {!!payment.viva_transaction_id && (
          <Row label={t('receipt.field_transactionCode')} value={payment.viva_transaction_id} styles={styles} />
        )}

        <View style={styles.divider} />
        <Text style={styles.note}>
          {payment.wallet_paid ? t('receipt.note_wallet') : t('receipt.note_viva')}
        </Text>
      </View>

      <ErrorText>{error}</ErrorText>

      <Button title={t('receipt.action_share')} variant="secondary" onPress={onShare} />
    </ScrollView>
  );
}

type RowStyles = ReturnType<typeof makeStyles>;

function Row({ label, value, onPress, styles }: { label: string; value: string; onPress?: () => void; styles: RowStyles }) {
  return (
    <View style={styles.row}>
      <Text style={styles.rowLabel}>{label}</Text>
      {onPress ? (
        <Pressable onPress={onPress} style={styles.rowValueWrap} accessibilityRole="button">
          <Text style={[styles.rowValue, styles.rowValueLink]} numberOfLines={1}>
            {value} ›
          </Text>
        </Pressable>
      ) : (
        <Text style={[styles.rowValue, styles.rowValueWrap]} numberOfLines={1}>
          {value}
        </Text>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
  flex: { flex: 1, backgroundColor: colors.background },
  loader: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
  },
  notFound: {
    color: colors.text,
    fontSize: fontSize.md,
  },
  content: {
    paddingHorizontal: spacing.lg,
    gap: spacing.lg,
  },
  topBar: { flexDirection: 'row' },
  back: {
    color: colors.primaryLight,
    fontSize: fontSize.md,
    fontWeight: '600',
  },
  receipt: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.lg,
    gap: spacing.md,
    ...shadow.card,
  },
  receiptHeader: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  brand: {
    color: colors.text,
    fontSize: fontSize.lg,
    fontWeight: '700',
  },
  receiptLabel: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    textTransform: 'uppercase',
    letterSpacing: 1,
    flexShrink: 1,
  },
  amount: {
    color: colors.text,
    fontSize: fontSize.xxl,
    fontWeight: '800',
    marginTop: spacing.xs,
  },
  status: {
    fontSize: fontSize.md,
    fontWeight: '700',
  },
  divider: {
    height: 1,
    backgroundColor: colors.border,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.md,
  },
  rowLabel: {
    color: colors.textMuted,
    fontSize: fontSize.md,
  },
  rowValueWrap: {
    flexShrink: 1,
  },
  rowValue: {
    color: colors.text,
    fontSize: fontSize.md,
    fontWeight: '600',
    textAlign: 'right',
  },
  rowValueLink: {
    color: colors.primaryLight,
  },
  note: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    lineHeight: 19,
  },
  error: {
    color: colors.danger,
    fontSize: fontSize.sm,
    textAlign: 'center',
  },
  });
}
